import { GetUserDocument, GetUserQuery, useSetTaskMutation } from "@graphql";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { createTaskId } from "@utils/task-id";
import { keyframes } from "@emotion/react";
import { useColorModeValue, useToast, useToken } from "@chakra-ui/react";
import { useUser } from "@utils";

export const TaskContext = createContext<{
  courseSlug?: string;
  courseLanguage?: string;
}>({});

export function useTask(taskId: string) {
  const toast = useToast();
  const timeoutRef = useRef<NodeJS.Timeout>();

  const { user, loading: loadingUser } = useUser();
  const [setTask, { loading }] = useSetTaskMutation({
    onCompleted() {
      clearInterval(timeoutRef.current);
    },
    update(cache, { data }) {
      if (data?.user?.task) {
        const { task } = data.user;

        const response = cache.readQuery<GetUserQuery>({
          query: GetUserDocument,
        });

        if (response) {
          const { me } = response;

          if (
            me?.__typename === "User" &&
            Array.isArray(me.tasks) &&
            !me.tasks.some(({ id: taskId }) => taskId === task.id)
          ) {
            cache.writeQuery<GetUserQuery>({
              query: GetUserDocument,
              data: {
                me: {
                  ...me,
                  tasks: [...me.tasks, task],
                },
              },
            });
          }
        }
      }
    },
  });

  const { courseSlug, courseLanguage } = useContext(TaskContext);
  const compositeTaskId = useMemo(
    () =>
      courseSlug
        ? createTaskId({
            taskId,
            courseId: courseSlug,
          })
        : taskId,
    [taskId, courseSlug]
  );

  const task = user?.tasks?.find((task) => task.id === compositeTaskId);

  return {
    task,
    isTaskComplete: Boolean(task?.completedAt),
    loadingUser,
    courseId: courseSlug,
    savingTask: loading,
    saveTask: (completedAt: Date | null, value?: string | null) => {
      if (user) {
        setTask({
          variables: {
            userId: user.id,
            task: {
              taskId: compositeTaskId,
              completedAt,
              value,
            },
            courseId: courseSlug,
            courseLanguage,
          },
        });

        // if progress saving is taking too long, show a toast
        timeoutRef.current = setTimeout(() => {
          toast({
            status: "info",
            title: "Saving your progress...",
          });
        }, 5000);
      }
    },
  };
}

export function useFlash() {
  const bg = useColorModeValue("gray.100", "whiteAlpha.200");
  const background = useToken("colors", bg);

  const flash = useMemo(
    () =>
      keyframes({
        from: {
          background: "transparent",
        },
        to: {
          background,
        },
      }),
    [background]
  );

  return {
    tabIndex: 0, // make the element programmatically focusable
    sx: {
      "&.flash": {
        // brief flashing animation that ends after 6 reps
        animationName: flash.toString(),
        animationDuration: "700ms",
        animationIterationCount: 6,
        animationTimingFunction: "ease-in-out",
        animationDirection: "alternate",
      },
    },
  };
}

export const MAX_TRIALS = 3;

export function useTrials() {
  const [trials, setTrials] = useState(0);
  const trialsRemaining = useMemo(() => MAX_TRIALS - trials, [trials]);

  const resetTrials = useCallback(
    (reset = true) => setTrials(reset ? 0 : (prev) => prev + 1),
    []
  );

  return {
    trialsRemaining,
    resetTrials,
  };
}
